import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { Divider, Table, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import {
    UserSwitchOutlined,
    CheckCircleOutlined,
    UndoOutlined,
    CheckOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import { formatDate } from 'src/utils/helpers';
import TableMainContent from 'src/components/table-main-content';
import SimpleTableContent from 'src/components/simple-table-content';
import useTableHoverRow from 'src/hooks/use-table-hover-row';
import './style.scss';
import LanguageFlagList from 'src/components/language-flag-list';
import usePermission from 'src/hooks/use-permission';

/**
 * Renders a Needs table component - it shows all attributes from a Need in each column of a row.
 * 
 * @param {Object} props - The component props.
 * @param {Array} props.data - The data for the needs table.
 * @param {Object} props.user - The logged user object.
 * @param {Function} props.onClickFit - The function to handle the fit button click.
 * @param {Function} props.onClickMatch - The function to handle the match button click.
 * @param {Function} props.onClickCancel - The function to handle the cancel match button click.
 * @returns {JSX.Element} The rendered needs table component.
 */
function NeedsTable({ data, user, onClickFit, onClickMatch, onClickCancel, onPaginationChange, pagination ={ current: 1, pageSize: 50} }) {
    const [expandedRows, setExpandedRows] = useState([]);
    const { key: hoverKey, onMove, onLeave } = useTableHoverRow();

    const permissions = usePermission();
    const { t } = useTranslation();

    /**
     * Handles the keydown event and scrolls to the next actions div when the 'a' key is pressed.
     * @param {KeyboardEvent} event - The keydown event.
     */
     //TODO - Validate if that this shortcut keys are good to go
     const handleKeyDown = (event) => {
        if (event.key === 'F1') {
            const actionsDivs = Array.from(
                document.querySelectorAll('[id^="actions"]')
            );
            const currentIndex = actionsDivs.findIndex(
                (div) => div === document.activeElement
            );
            const nextIndex = currentIndex + 1;
            const nextActionsDiv = actionsDivs[nextIndex];
            if (nextActionsDiv) {
                nextActionsDiv.scrollIntoView({ behavior: 'smooth' });
                nextActionsDiv.focus();
            } else if (currentIndex === actionsDivs.length - 1) {
                actionsDivs[0].scrollIntoView({ behavior: 'smooth' });
                actionsDivs[0].focus();
            }
        }
    };

    /**
     * Adds an event listener for the keydown event and removes it when the component unmounts.
     */
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const columns = [
        {
            title: t('requisitionName'),
            dataIndex: 'title',
            key: 'title',
            width: 180,
            sorter: (a, b) => {
                const titleA = a.title;
                const titleB = b.title;
                return titleA.localeCompare(titleB);
            },
            render: (text, item) => (
                <>
                    <TableMainContent
                        mainContent={item.title}
                        mainContentAriaLabel={item.title}
                        translationMainContent={t('requisitionName')}
                        subContent={`#${item.requisitionCode}`}
                        subContentAriaLabel={item.requisitionCode}
                        translationSubContent={t('requisitionCode')}
                    />
                </>
            ),
        },
        {
            title: t('role'),
            dataIndex: 'role',
            key: 'role',
            width: 120,
            sorter: (a, b) => {
                const roleA = a.role;
                const roleB = b.role;
                return roleA.localeCompare(roleB);
            },
            render: (text, item) => (
                <>
                    <TableMainContent 
                        mainContent={item.role}
                        mainContentAriaLabel={item.role}
                        translationMainContent={t('role')}
                        subContent={item.level}
                        subContentAriaLabel={item.level}
                        translationSubContent={t('positionMap')}
                    />
                </>
            ),
        },
        {
            title: t('tech'),
            width: 120,
            dataIndex: 'technology',
            key: 'technology',
            sorter: (a, b) => {
                const localA = a.technology;
                const localB = b.technology;
                return localA.localeCompare(localB);
            },
            render: (text, item) => {
                return (
                    <>
                        <SimpleTableContent 
                            mainContent={item.technology} 
                            mainContentAriaLabel={item.technology}
                            translationMainContent={t('tech')}
                        />
                        {item.englishLevel && (
                            <LanguageFlagList 
                                englishLevel={item.englishLevel}
                            />
                        )}
                    </>
                );
            },
        },
        {
            title: `GU / ${t('alliance')}`,
            dataIndex: 'gu',
            key: 'gu',
            width: 100,
            sorter: (a, b) => {
                const localA = a.team;
                const localB = b.team;
                return localA.localeCompare(localB);
            },
            render: (text, { team }) => (
                <SimpleTableContent 
                    mainContent={team ?? '-'}
                    mainContentAriaLabel={team === '-' ? t('hasNoInformation') : team}
                    translationMainContent={t('guAlliance')}
                />
            ),
        },
        {
            title: t('client'),
            dataIndex: 'client',
            key: 'client',
            width: 120,
            sorter: (a, b) => {
                const clientA = a.client;
                const clientB = b.client;
                return clientA.localeCompare(clientB);
            },
            render: (text, { client }) => (
                <SimpleTableContent 
                    mainContent={client ?? '-'}
                    mainContentAriaLabel={client === '-' ? t('hasNoInformation') : client}
                    translationMainContent={t('client')}
                />
            ),
        },
        {
            title: t('site'),
            dataIndex: 'site',
            key: 'site',
            width: 120,
            sorter: (a, b) => {
                const localA = a.site;
                const localB = b.site;
                return localA.localeCompare(localB);
            },
            render: (text, { site }) => (
                <SimpleTableContent 
                    mainContent={site ?? '-'}
                    mainContentAriaLabel={site === '-' ? t('hasNoInformation') : site}
                    translationMainContent={t('site')}
                />
            ),
        },
        {
            title: t('talentAttraction'),
            dataIndex: 'requisitionTalentAttraction',
            key: 'requisitionTalentAttraction',
            width: 100,
            sorter: (a, b) => {
                const localA = a.requisitionTalentAttraction;
                const localB = b.requisitionTalentAttraction;
                return localA.localeCompare(localB);
            },
            render: (text, { requisitionTalentAttraction }) => (
                <SimpleTableContent 
                    mainContent={requisitionTalentAttraction ?? '-'}
                    mainContentAriaLabel={requisitionTalentAttraction === '-' ? t('hasNoInformation') : requisitionTalentAttraction}
                    translationMainContent={t('ta')}
                />
            ),
        },
        {
            title: t('fitStatus'),
            dataIndex: 'deallocations',
            key: 'status',
            width: 130,
            defaultSortOrder: 'ascend',
            sorter: (need1, need2) => {
                const reg1People = need1.deallocations.length;
                const reg2People = need2.deallocations.length;
                if (reg1People === 1 && reg2People === 1) return 0;
                if (reg1People === 1) return -1;
                if (reg2People === 1) return 1;

                if (reg1People > 0 && reg2People > 0) return 0;
                if (reg1People > 0) return -1;
                if (reg2People > 0) return 1;

                return 0;
            },
            render: (text, item) => {
                let fit, fitClass;
                const people = item.deallocations
                    .filter((people) => people.active === 'Y')
                    .sort((a, b) => new Date(b.Fits.createdAt) - new Date(a.Fits.createdAt));
                let formattedUpdatedAt = formatDate(people[0]?.Fits?.createdAt);
                let subContent = t('lastUpdatedOn', {formattedUpdatedAt});
                if (people.length === 0) {
                    fit = t('withoutFitNeed');
                    fitClass = 'pending';
                    subContent = null;
                } else if (people.length === 1 && people[0]?.Fits?.match === 'Y') {
                    fit = 'Match';
                    fitClass = 'match';
                    subContent = `${t('with')} ${people[0].collaborator_deallocation?.login}`;
                } else if (people.length === 1 && people[0]?.Fits?.match === 'N') {
                    fit = `${people.length} Fit`;
                    fitClass = 'match';
                } else if (people.length > 1 && people[0]?.Fits?.match === 'N') {
                    fit = `${people.length} Fits`;
                    fitClass = 'match';
                }
                return ( <>
                    {fitClass === 'pending' && (
                        <SimpleTableContent
                            mainContentClass={fitClass}
                            mainContent={fit}
                            mainContentAriaLabel={fit}
                            translationMainContent={t('fitStatus')}
                        />
                    )}
                    { fitClass !== 'pending' && (
                        <TableMainContent
                            mainContentClass={fitClass}
                            mainContent={fit}
                            mainContentAriaLabel={fit}
                            translationMainContent={t('fitStatus')}
                            subContent={subContent}
                            subContentAriaLabel={subContent}
                            translationSubContent=''
                            fitDetails={fit !== 'Match' && { logins: people?.map(p => `@${p?.collaborator_deallocation?.login}`)}}
                        />
                    )}
                </>
                );
            },
        },
        {
            title: t('interviewRequired'),
            dataIndex: 'interviewRequired',
            key: 'interviewRequired',
            width: 100,
            sorter: (a, b) => {
                const localA = a.interviewRequired;
                const localB = b.interviewRequired;
                return localA.localeCompare(localB);
            },
            render: (text, { interviewRequired }) => (
                <SimpleTableContent 
                    mainContent={interviewRequired === 'Y' 
                    ? 
                    (<Tooltip title={t("interview-yes")}>
                        <CheckOutlined className='icon-interview-required--yes' role='note' aria-label={t("interview-yes")} />
                    </Tooltip>)
                    : 
                    (<Tooltip title={t("interview-no")}>
                        <CloseOutlined className='icon-interview-required--no' role='note' aria-label={t("interview-no")} />
                    </Tooltip>)
                    }
                    mainContentAriaLabel={!interviewRequired 
                        ? t('hasNoInformation') 
                        : (interviewRequired === 'Y' ? t("interview-yes") : t("interview-no"))
                    }
                />
            ),
        },

        {
            title: t('actions'),
            dataIndex: 'actions',
            key: 'actions',
            fixed: 'right',
            className: 'opportunity-table__actions',
            width: 140,
            render: (text, need) => {
                const allowedEmailsToMatch = [need.requisitionTalentAttractionEmail, need.requisitionManagerEmail]
                const isOwner = user.profile === 'ADM' || (permissions.matchPermissions?.canCreate && allowedEmailsToMatch.includes(user.email));
                
                const hasFit = need.deallocations.length > 0;
                let matchDeallocationId;

                if (hasFit) {
                    matchDeallocationId = need.deallocations
                    .find(dealloc => dealloc?.Fits?.match === 'Y' && dealloc?.Fits?.active === 'Y')?.id;
                }
                const hasMatch = hasFit ? 
                    matchDeallocationId :
                    false;
                const canFit = hasFit && !hasMatch;

                return (
                    <div 
                        id='actions'
                        onKeyDown={handleKeyDown( need)} 
                        tabIndex={0} 
                        aria-label={t("actionsGroupOpportunity", {title: need.title})}
                        role='group'
                        className='opportunity-table__actions_group'
                    >
                        { (hasMatch && isOwner) &&
                            <Tooltip placement="topRight" title={t('cancelMatch')}>
                                <UndoOutlined 
                                    style={{ fontSize: 20, color: '#4a4a4a' }} 
                                    twoToneColor onClick={() => onClickCancel({deallocationId: matchDeallocationId, matchNeedId: need.id}) } 
                                    role='button'
                                    aria-label={t('cancelMatch')}
                                    tabIndex={0}
                                    id='cancel-match'
                                    type='button'
                                    title={t('cancelMatch')}
                                />
                            </Tooltip>
                        }
                        { (canFit && isOwner) &&
                            <Tooltip placement="topRight" title={t('confirmMatch')}>
                                <CheckCircleOutlined 
                                    style={{ fontSize: 20, color: '#27AE60' }} 
                                    twoToneColor onClick={() => onClickMatch(need) }
                                    role='button'
                                    aria-label={t('confirmMatch')}
                                    tabIndex={0}
                                    id='confirm-match'
                                    type='button'
                                    title={t('confirmMatch')}
                                    />
                            </Tooltip>
                        }
                        { (!hasMatch) && <Tooltip placement="topRight" title={t('requestViewFits')}>
                            <UserSwitchOutlined
                                style={{ fontSize: 20, color: '#4a4a4a' }}
                                onClick={() => onClickFit(need)}
                                role='button'
                                aria-label={t('requestViewFits')}
                                tabIndex={0}
                                id='request-view-fits'
                                type='button'
                                title={t('requestViewFits')}
                            />
                        </Tooltip>
                        }
                    </div>
                ) 
            },
        },
    ];

    return (
        <Table
            columns={columns}
            scroll={{
                x: 800,
            }}
            rowKey='id'
            className='opportunity-table'
            pagination={{
                showSizeChanger: true,
                defaultPageSize: pagination.pageSize,
                current: pagination.current,
                onChange: (page, pageSize) => {
                onPaginationChange && onPaginationChange({
                    limit: pageSize,
                    offset: (page - 1) * pageSize
                });
                }
            }}
            onRow={(item) => ({
                onMouseLeave: onLeave,
                onMouseMove: () => onMove(item.id),
            })}
            expandable={{
                expandedRowRender: (record) => {
                    const needHasDeallocations = record.deallocations.length > 0;
                    const hasMatch = record.deallocations.some(d => d.Fits.match === 'Y') 

                    let loginsWithFit;

                    if (needHasDeallocations && !hasMatch) {
                        loginsWithFit = record.deallocations.map((d, i, a) => `@${d.collaborator_deallocation.login}${i + 1 === a.length? '' : ','}`)
                    }

                    return (
                        <div aria-label={t('moreInfoOpportunity', { opportunity: record.title })} id='description' role='group'>
                            <div className="opportunity-table__content_description" tabIndex={0} aria-labelledby='description'>
                                <div onMouseMove={() => onMove(record.id)} onMouseLeave={onLeave}>
                                    {loginsWithFit && <p>
                                        <div><strong>Fit Logins: </strong></div> 
                                        {loginsWithFit.map(l => <span>{l}<br /></span>)}
                                    </p>}
                                    {record.requisitionManager && <p>
                                        <div><strong>{t('hiringManager')}:</strong></div> 
                                        {record.requisitionManager}
                                    </p>}
                                    {record.pdm && <p>
                                        <div><strong>Business Partner:</strong></div>
                                        {record.pdm}
                                    </p>}
                                </div>
                                <div className='opportunity-table__scrollable'>
                                    {record.description && <p>
                                        <div><strong>{t('notesOptional')}:</strong></div>
                                    {record.description}</p>}
                                </div>
                            </div>
                        </div>
                    );
                },
                expandIcon: ({ expanded, onExpand, record }) => {
                    return (
                        expanded ? (
                    <button 
                    type="button" 
                    className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded" 
                    aria-label={t("collapseRow", {collapsedValue: record.title})} 
                    onClick={(e) => onExpand(record, e)}
                    tabIndex={0}
                    />
                    ) : (
                        <button
                            type='button'
                            className='ant-table-row-expand-icon ant-table-row-expand-icon-collapsed'
                            aria-label={t('expandRow', { expandedValue: record.title })}
                            onClick={(e) => onExpand(record, e)}
                            tabIndex={0}
                        />
                    )
                )},
                rowExpandable: (record) => record.description.trim() !== '',
                defaultExpandedRowKeys: new Array(data.length)
                    .fill(null)
                    .map((v, i) => i),
                onExpandedRowsChange: (expandedRows) => {
                    setExpandedRows(expandedRows);
                },
            }}
            rowClassName={(item, index) =>
                classnames({
                    'hovered-row': hoverKey === item.id,
                    'expanded-row': expandedRows.includes(item.id),
                    'row-even': index % 2 === 0,
                    'row-odd': index % 2 !== 0
                })
            }
            dataSource={data}
        />
    );
}

export default NeedsTable;
